import { useEffect, useRef, useState } from 'react';
import {
  Box, CircularProgress, Collapse, Drawer, Fab, IconButton, InputAdornment, Stack,
  TextField, Tooltip, Typography, useMediaQuery,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import StopIcon from '@mui/icons-material/Stop';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ThumbUpOffAltIcon from '@mui/icons-material/ThumbUpOffAlt';
import ThumbDownOffAltIcon from '@mui/icons-material/ThumbDownOffAlt';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';
import { piper } from '../../theme/theme';
import { ChatBubble, LiveStatus, PiperAvatar } from '../../components/piper/primitives';
import PiperActionCard from '../../components/piper/PiperActionCard';
import useLoanChat from '../../hooks/useLoanChat';
import useScenarioChat from '../../hooks/useScenarioChat';
import usePiperFeatures from '../../hooks/usePiperFeatures';
import { MAX_MESSAGE_LENGTH } from '../../services/loanChatApi';
import { usePiperConsent } from './PiperConsentContext';
import { usePiperRailContext } from './PiperRailContext';

export const PIPER_RAIL_WIDTH = 340;

/** Markdown styling tuned for the 340px rail — tables must not overflow. */
const markdownSx = {
  '& p': { m: 0, mb: 0.5 },
  '& p:last-of-type': { mb: 0 },
  '& ul, & ol': { pl: 2, my: 0.5 },
  '& li': { mb: 0.25 },
  '& table': { borderCollapse: 'collapse', width: '100%', my: 0.75, fontSize: '11.5px', display: 'block', overflowX: 'auto' },
  '& th, & td': { border: `1px solid ${piper.hairlineStrong}`, px: 0.75, py: 0.4, textAlign: 'left' },
  '& th': { backgroundColor: piper.subtleFill, fontWeight: 700 },
  '& code': { backgroundColor: piper.subtleFill, px: 0.4, borderRadius: '3px', fontSize: '11px' },
  '& pre': { overflowX: 'auto', backgroundColor: piper.subtleFill, p: 1, borderRadius: '6px', my: 0.75 },
  '& pre code': { backgroundColor: 'transparent', p: 0 },
};

const QuestionChip = ({ label, onClick }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    sx={{
      cursor: 'pointer',
      borderRadius: `${piper.radius.pill}px`,
      border: `1px solid ${piper.outline}`,
      backgroundColor: piper.surface,
      color: piper.inkSecondary,
      px: 1.4,
      py: 0.5,
      fontSize: '11px',
      fontWeight: 600,
      fontFamily: piper.font,
      textAlign: 'left',
      transition: 'background-color .15s ease',
      '&:hover': { backgroundColor: piper.subtleFill2 },
    }}
  >
    {label}
  </Box>
);

const Markdown = ({ children }) => (
  <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeSanitize]}>{children}</ReactMarkdown>
);

const Message = ({ role, children }) => (
  <Box
    sx={{
      alignSelf: role === 'user' ? 'flex-end' : 'flex-start',
      maxWidth: '92%',
      borderRadius: role === 'user' ? '12px 12px 4px 12px' : piper.radius.bubble,
      backgroundColor: role === 'user' ? piper.subtleFill : piper.greenBg2,
      border: role === 'user' ? 'none' : `1px solid ${piper.greenBorder}`,
      px: 1.5,
      py: 1,
      fontSize: '12.5px',
      lineHeight: 1.55,
      color: piper.ink,
      ...markdownSx,
    }}
  >
    {children}
  </Box>
);

const RailIconButton = ({ title, onClick, disabled, children, active }) => (
  <Tooltip title={title}>
    <span>
      <IconButton
        size="small"
        onClick={onClick}
        disabled={disabled}
        aria-label={title}
        sx={{ p: 0.4, color: active ? piper.green2 : piper.muted45, '& svg': { fontSize: 14 } }}
      >
        {children}
      </IconButton>
    </span>
  </Tooltip>
);

/** Copy / feedback / regenerate row under an assistant message. */
const MessageActions = ({ content, messageId, feedback, onFeedback, onRegenerate, canRegenerate, feedbackEnabled }) => {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard unavailable — nothing useful to show in the rail
    }
  };

  return (
    <Stack direction="row" spacing={0.25} alignItems="center" sx={{ pl: 0.5, mt: -0.25 }}>
      <RailIconButton title={copied ? 'Copied' : 'Copy'} onClick={copy} active={copied}>
        <ContentCopyIcon />
      </RailIconButton>
      {canRegenerate && (
        <RailIconButton title="Regenerate" onClick={onRegenerate}>
          <RefreshIcon />
        </RailIconButton>
      )}
      {feedbackEnabled && (
        <>
          <RailIconButton title="Helpful" onClick={() => onFeedback(1, messageId)} active={feedback === 1}>
            <ThumbUpOffAltIcon />
          </RailIconButton>
          <RailIconButton title="Not helpful" onClick={() => onFeedback(-1, messageId)} active={feedback === -1}>
            <ThumbDownOffAltIcon />
          </RailIconButton>
        </>
      )}
    </Stack>
  );
};

/** Collapsible quick-action presets, one column to fit 340px. */
const QuickActions = ({ open, onToggle, categories, isLoading, error, onSelect, disabled }) => (
  <Box sx={{ mb: 1.25 }}>
    <Box
      component="button"
      type="button"
      onClick={onToggle}
      sx={{
        width: '100%', display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer',
        border: 'none', background: 'none', p: 0, mb: 0.75,
        fontFamily: piper.font, fontSize: '10.5px', fontWeight: 700,
        letterSpacing: '.06em', textTransform: 'uppercase', color: piper.muted45,
      }}
      aria-expanded={open}
    >
      Quick actions
      <ExpandMoreIcon sx={{ fontSize: 14, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s ease' }} />
    </Box>
    <Collapse in={open} unmountOnExit>
      {isLoading && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 0.5 }}>
          <CircularProgress size={11} />
          <Typography sx={{ fontSize: '11px', color: piper.muted45 }}>Loading quick actions…</Typography>
        </Stack>
      )}
      {error && <Typography sx={{ fontSize: '11px', color: piper.redDark }}>{error}</Typography>}
      <Stack spacing={1}>
        {categories.map((category) => (
          <Box key={category.categoryKey}>
            <Typography sx={{ fontSize: '10px', fontWeight: 700, color: piper.muted45, mb: 0.5 }}>
              {category.categoryLabel}
            </Typography>
            <Stack spacing={0.5}>
              {(category.presets || []).map((preset) => (
                <Tooltip key={preset.presetKey} title={preset.description || ''} placement="left">
                  <Box
                    component="button"
                    type="button"
                    disabled={disabled}
                    onClick={() => onSelect(preset)}
                    sx={{
                      cursor: disabled ? 'default' : 'pointer',
                      textAlign: 'left',
                      width: '100%',
                      borderRadius: `${piper.radius.card}px`,
                      border: `1px solid ${piper.outline}`,
                      backgroundColor: piper.surface,
                      color: piper.inkSecondary,
                      px: 1, py: 0.6,
                      fontFamily: piper.font, fontSize: '11px', fontWeight: 600,
                      opacity: disabled ? 0.5 : 1,
                      '&:hover': { backgroundColor: disabled ? piper.surface : piper.subtleFill2 },
                    }}
                  >
                    {preset.label}
                  </Box>
                </Tooltip>
              ))}
            </Stack>
          </Box>
        ))}
      </Stack>
    </Collapse>
  </Box>
);

/** The rail body — shared between the docked rail and the <1280px drawer. */
const RailBody = ({ config, onClose }) => {
  const {
    surface, status, briefing, cards = [], questions = [], onAction, loanId, extra,
    scenarioChat, getScenarioSnapshot,
  } = config;
  const features = usePiperFeatures();
  const { requireConsent, openTermsModal, consentRequired } = usePiperConsent();
  const [doneCards, setDoneCards] = useState({});
  const [quickActionsOpen, setQuickActionsOpen] = useState(false);
  const scrollRef = useRef(null);

  const loanChatEnabled = Boolean(loanId) && features.chat;
  const scenarioChatEnabled = Boolean(scenarioChat) && !loanId
    && features.chat && features.scenarioChat;
  const chatEnabled = loanChatEnabled || scenarioChatEnabled;

  const loanChat = useLoanChat({
    loanId: loanChatEnabled ? loanId : null,
    isOpen: loanChatEnabled,
    quickActionsEnabled: features.chatQuickActions,
    quickActionsExpanded: quickActionsOpen,
    scenarioEnabled: features.chatScenarios,
  });

  const scenarioChatHook = useScenarioChat({
    enabled: scenarioChatEnabled,
    isOpen: scenarioChatEnabled,
    getScenarioSnapshot,
    quickActionsEnabled: features.chatQuickActions,
    quickActionsExpanded: quickActionsOpen,
  });

  const chat = loanChatEnabled ? loanChat : scenarioChatHook;

  const {
    messages, input, setInput, streamingContent, statusMessage, isStreaming,
    isLoadingHistory, isLoadingPresets, historyError, presetsError, error,
    presetCategories, feedbackByMessageId, canRegenerate,
    sendMessage, regenerate, stopStreaming, clearChat, sendPreset, submitFeedback,
  } = chat;

  const gatedSendMessage = () => requireConsent(() => sendMessage());
  const gatedSendPreset = (...args) => requireConsent(() => sendPreset(...args));
  const gatedRegenerate = () => requireConsent(() => regenerate());

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages.length, streamingContent, doneCards]);

  const handleCardAction = (card, action) => {
    // Navigation-only actions (e.g. Open loan) must not flash a "Done" state.
    if (action.kind !== 'tertiary' && action.completes !== false) {
      setDoneCards((prev) => ({ ...prev, [card.id]: `Done — ${action.label.toLowerCase()} handled, Piper is on it` }));
    }
    onAction?.(card, action);
  };

  const overLimit = input.length > MAX_MESSAGE_LENGTH;
  const showCounter = input.length > MAX_MESSAGE_LENGTH * 0.8;
  const lastIndex = messages.length - 1;
  const showQuickActions = chatEnabled && features.chatQuickActions;
  const showQuestionChips = chatEnabled && !showQuickActions && questions.length > 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', backgroundColor: piper.surface }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={1.25} sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${piper.hairline}`, flexShrink: 0 }}>
        <PiperAvatar size={32} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontSize: '13px', fontWeight: 700, color: piper.ink, lineHeight: 1.2 }}>Piper</Typography>
          {status && <LiveStatus>{status}</LiveStatus>}
        </Box>
        {chatEnabled && messages.length > 0 && (
          <RailIconButton title="Clear conversation" onClick={clearChat} disabled={isStreaming}>
            <DeleteOutlineIcon />
          </RailIconButton>
        )}
        {onClose && (
          <IconButton size="small" onClick={onClose} aria-label="close Piper panel">
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Stack>

      {/* Scrollable body */}
      <Box ref={scrollRef} sx={{ flex: 1, overflowY: 'auto', px: 2, py: 1.75 }}>
        <Stack spacing={1.25}>
          {consentRequired && (
            <Box
              component="button"
              type="button"
              onClick={() => openTermsModal()}
              sx={{
                cursor: 'pointer',
                textAlign: 'left',
                borderRadius: `${piper.radius.card}px`,
                border: `1px solid ${piper.greenBorder}`,
                backgroundColor: piper.greenBg2,
                px: 1.25,
                py: 1,
                fontFamily: piper.font,
              }}
            >
              <Typography sx={{ fontSize: '12px', fontWeight: 700, color: piper.ink, mb: 0.25 }}>
                Review Piper terms to enable
              </Typography>
              <Typography sx={{ fontSize: '11px', color: piper.inkSecondary, lineHeight: 1.45 }}>
                Accept the terms of use to unlock loan chat, pricing insights, and Piper suggestions.
              </Typography>
            </Box>
          )}
          {briefing && <ChatBubble>{briefing}</ChatBubble>}
          {cards.map((card) => (
            <PiperActionCard key={card.id} card={card} done={doneCards[card.id]} onAction={(action) => handleCardAction(card, action)} />
          ))}
          {extra}

          {isLoadingHistory && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 0.5 }}>
              <CircularProgress size={12} />
              <Typography sx={{ fontSize: '11px', color: piper.muted45 }}>Loading the conversation…</Typography>
            </Stack>
          )}
          {historyError && (
            <Typography sx={{ fontSize: '11px', color: piper.redDark, px: 0.5 }}>{historyError}</Typography>
          )}

          {(messages.length > 0 || streamingContent) && (
            <Stack spacing={1} sx={{ pt: 0.5 }}>
              {messages.map((m, i) => (
                <Box key={m.id ?? i} sx={{ display: 'flex', flexDirection: 'column', alignItems: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
                  <Message role={m.role}><Markdown>{m.content}</Markdown></Message>
                  {m.role === 'assistant' && !isStreaming && (
                    <MessageActions
                      content={m.content}
                      messageId={m.id}
                      feedback={feedbackByMessageId[m.id]}
                      onFeedback={submitFeedback}
                      onRegenerate={gatedRegenerate}
                      canRegenerate={canRegenerate && i === lastIndex}
                      feedbackEnabled={features.chatFeedback}
                    />
                  )}
                </Box>
              ))}
              {streamingContent && (
                <Message role="assistant"><Markdown>{streamingContent}</Markdown></Message>
              )}
              {isStreaming && (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 0.5 }}>
                  <CircularProgress size={12} />
                  <Typography sx={{ fontSize: '11px', color: piper.muted45 }}>
                    {statusMessage || 'Piper is thinking…'}
                  </Typography>
                </Stack>
              )}
            </Stack>
          )}

          {error && (
            <Box sx={{ borderRadius: `${piper.radius.card}px`, border: `1px solid ${piper.redTintBorder}`, backgroundColor: piper.redTintBg, px: 1.25, py: 0.75 }}>
              <Typography sx={{ fontSize: '11.5px', color: piper.redDark }}>{error}</Typography>
            </Box>
          )}
        </Stack>
      </Box>

      {/* Quick actions / question chips / input pinned at the bottom */}
      {chatEnabled && (
        <Box sx={{ px: 2, py: 1.5, borderTop: `1px solid ${piper.hairline}`, flexShrink: 0 }}>
          {showQuickActions && (
            <QuickActions
              open={quickActionsOpen}
              onToggle={() => setQuickActionsOpen((v) => !v)}
              categories={presetCategories}
              isLoading={isLoadingPresets}
              error={presetsError}
              disabled={isStreaming}
              onSelect={(preset) => {
                setQuickActionsOpen(false);
                gatedSendPreset(preset.promptTemplate, preset.intent, preset.presetKey);
              }}
            />
          )}
          {showQuestionChips && (
            <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.75} sx={{ mb: 1.25 }}>
              {questions.map((q) => (
                <QuestionChip key={q} label={q} onClick={() => gatedSendPreset(q, '', '')} />
              ))}
            </Stack>
          )}
          <TextField
            fullWidth
            size="small"
            multiline
            maxRows={4}
            placeholder={surface === 'loan'
              ? 'Ask Piper, or tell her what to do…'
              : surface === 'pricer'
                ? 'Ask about this scenario…'
                : 'Ask Piper…'}
            value={input}
            error={overLimit}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                if (!overLimit) gatedSendMessage();
              }
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {isStreaming ? (
                    <IconButton size="small" aria-label="stop generating" onClick={stopStreaming}>
                      <StopIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  ) : (
                    <IconButton
                      size="small"
                      aria-label="send to Piper"
                      disabled={!input.trim() || overLimit}
                      onClick={gatedSendMessage}
                    >
                      <SendIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  )}
                </InputAdornment>
              ),
            }}
          />
          {showCounter && (
            <Typography sx={{ mt: 0.5, textAlign: 'right', fontSize: '10.5px', color: overLimit ? piper.redDark : piper.muted45 }}>
              {input.length} / {MAX_MESSAGE_LENGTH}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

/**
 * PiperRail — the 340px right rail. Docked at ≥1280px; collapses into a
 * toggleable drawer below that. Renders only when a page has published rail
 * content via usePiperRail.
 */
const PiperRail = () => {
  const { config } = usePiperRailContext();
  const { requireConsent } = usePiperConsent();
  const isWide = useMediaQuery('(min-width:1280px)');
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Hide the rail column when a page published nothing useful (no chat, briefing, cards, questions, or extra).
  const hasContent = Boolean(
    config
    && (config.loanId
      || config.scenarioChat
      || config.briefing
      || (config.cards && config.cards.length > 0)
      || (config.questions && config.questions.length > 0)
      || config.extra),
  );
  if (!hasContent) return null;

  if (isWide) {
    return (
      <Box
        component="aside"
        aria-label="Piper assistant"
        sx={{
          width: PIPER_RAIL_WIDTH,
          flexShrink: 0,
          borderLeft: `1px solid ${piper.hairlineStrong}`,
          alignSelf: 'stretch',
          height: '100%',
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        <RailBody config={config} />
      </Box>
    );
  }

  return (
    <>
      <Tooltip title="Open Piper">
        <Fab
          size="medium"
          onClick={() => requireConsent(() => setDrawerOpen(true))}
          aria-label="open Piper panel"
          sx={{
            position: 'fixed',
            right: 16,
            bottom: 16,
            p: 0,
            overflow: 'hidden',
            backgroundColor: '#fff',
            boxShadow: '0 4px 14px rgba(0,0,0,0.18)',
            '&:hover': { backgroundColor: '#fff', opacity: 0.94 },
          }}
        >
          <PiperAvatar size={48} />
        </Fab>
      </Tooltip>
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        slotProps={{ paper: { sx: { width: PIPER_RAIL_WIDTH } } }}
      >
        <RailBody config={config} onClose={() => setDrawerOpen(false)} />
      </Drawer>
    </>
  );
};

export default PiperRail;
